import {
    Box,
    Button,
    createStyles,
    Chip,
    CircularProgress,
    Tooltip,
    Typography,
    Grid,
    IconButton,
    makeStyles,
    Paper,
    Radio,
    TableContainer,
} from "@material-ui/core";
import {Dispatch, Identifier, ListBase, useQueryWithStore, useRefresh, useUpdate,} from "ra-core";
import {
    BooleanInput,
    Datagrid,
    DateField,
    DeleteWithConfirmButton,
    Empty,
    FilterButton,
    FunctionField,
    ListToolbar,
    NullableBooleanInput,
    ReferenceInput,
    TextField,
    TextInput,
    TopToolbar,
} from "ra-ui-materialui";
import {SetStateAction, useEffect, useState} from "react";
import {AutocompleteInput, DateInput, DateTimeInput, Record, useNotify} from "react-admin";
import SmallPagination from "../../components/SmallPagination";
import {SubjectField} from "../../components/SubjectField";
import {ReferenceInputSubject} from "../../components/reference-inputs/ReferenceInputSubject";
import {useClosedSidebar} from "../../shared/hooks/useClosedSdebar";
import {useOptions} from "../../shared/hooks/useOptions";
import {initField} from "../../shared/utils";
import {NoteImage} from "./NoteImage";
import {IdentifierField} from "../../components/IdentifierField";
import RotateRightIcon from '@material-ui/icons/RotateRight';
import {BsStars} from "react-icons/bs";
import {client} from "../../dataProvider";

const DeliveryList = ({
                          selectedNote,
                          setSelectedNote,
                      }: {
    selectedNote?: Identifier;
    setSelectedNote: Dispatch<SetStateAction<Identifier | undefined>>;
}) => {
    const {data: options} = useOptions("deliveries", "GET");
    const [update] = useUpdate();
    const refresh = useRefresh();


    function getAYesterdaysDate() {
        let d = new Date();
        d.setTime((d.getTime() - (48 * 60 * 60 * 1000)));
        return d.toISOString();
    }

    const filters = options
        ? [
            <TextInput {...initField("id", options)} />,
            <TextInput {...initField("document_number", options)} alwaysOn/>,
            <DateInput {...initField("document_date", options)} />,
            <ReferenceInputSubject {...initField("receiver", options)} />,
            <ReferenceInputSubject
                {...initField("client", options)}
                filter={{type: "client"}}
            />,
            <ReferenceInput
                source="route"
                label="Giro"
                reference="autocomplete/route"
            >
                <AutocompleteInput optionText="label"/>
            </ReferenceInput>,
            <ReferenceInput
                {...initField("import_file", options)}
                reference="autocomplete/imported-files"
            >
                <AutocompleteInput optionText="label"/>
            </ReferenceInput>,
            <DateInput
                {...initField("delivery_date_day", options)}
                label="Data missione"
            />,
            <DateTimeInput
                {...initField("delivery_date_after", options)}
                label="Data missione dal"
            />,
            <DateTimeInput
                {...initField("delivery_date_before", options)}
                label="Data missione fino al"
            />,
            <BooleanInput {...initField("bill_sent", options)} />,
            <NullableBooleanInput
                alwaysOn
                label="Contiene bolle"
                source="has_notes"
            />,
            <NullableBooleanInput alwaysOn {...initField("no_notes", options)} label="Senza bolle attivo" />,
        ]
        : [];

    return options ? (
        <ListBase
            resource="deliveries"
            syncWithLocation={true}
            basePath="/deliveries"
            filterDefaultValues={{
                has_notes: false,
                delivery_date_after: getAYesterdaysDate()
            }}
            sort={{field: 'id', order: 'DESC'}}
            perPage={25}
        >
            <Box p={1}>
                <ListToolbar
                    filters={filters}
                    actions={
                        <TopToolbar>
                            <FilterButton/>
                        </TopToolbar>
                    }
                />
            </Box>
            <Box component={Paper}>
                <TableContainer>
                    <Button onClick={() => {
                        window.open('/deliveries/create', '_blanck');
                    }}>Crea missione</Button>

                    <Datagrid>
                        <FunctionField
                            render={(record: Record | undefined) => {
                                if (record) {
                                    return (
                                        <Button
                                            disabled={!selectedNote}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const check = window.confirm("Sei sicuro?");
                                                if (check) {
                                                    update(
                                                        {
                                                            resource: "notes",
                                                            payload: {
                                                                id: selectedNote,
                                                                data: {delivery: record.id},
                                                            },
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                setSelectedNote(undefined);
                                                                refresh();
                                                            },
                                                        }
                                                    );
                                                }
                                            }}
                                            color="primary"
                                        >
                                            Associa
                                        </Button>
                                    );
                                }
                                return null;
                            }}
                        />
                        <IdentifierField {...initField("id", options)} />
                        <TextField {...initField("document_number", options)} />
                        <SubjectField {...initField("client_object", options)} sortBy="client__business_name"/>
                        <SubjectField {...initField("receiver_object", options)} sortBy="receiver__business_name"/>
                        <DateField {...initField("document_date", options)} />
                        <DateField {...initField("delivery_date", options)} />
                    </Datagrid>
                </TableContainer>
                <SmallPagination/>
            </Box>
        </ListBase>
    ) : null;
};

const aiReasons: {[key: string]: string} = {
    missing_document_number: "Numero documento non rilevato",
    receiver_solver_error: "Dati estratti: errore nel riconoscimento del destinatario, associa manualmente",
    receiver_not_found: "Destinatario non riconosciuto: associa manualmente",
    probable_deliveries: "Missioni probabili: verifica i dati e conferma l’associazione",
    multiple_deliveries: "Più missioni corrispondenti: associa manualmente",
};

const UnassignedNoteCard = ({note, selected, onSelect}: {
    note: Record;
    selected: boolean;
    onSelect: () => void;
}) => {
    const [currentNote, setCurrentNote] = useState(note);
    const [rotation, setRotation] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [assigningDelivery, setAssigningDelivery] = useState<Identifier>();
    const refresh = useRefresh();
    const notify = useNotify();
    const processing = ["QU", "IP"].includes(currentNote.status_match_delivery_ai);
    const result = currentNote.ai_match;
    const receiver = result && result.receiver_data;

    useEffect(() => {
        setCurrentNote(previous => {
            if (previous.id !== note.id) return note;
            const incomingUpdate = new Date(note.last_update || 0).getTime();
            const currentUpdate = new Date(previous.last_update || 0).getTime();
            return incomingUpdate > currentUpdate ? note : previous;
        });
    }, [note]);

    useEffect(() => {
        if (!processing || submitting) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        const poll = async () => {
            try {
                const updated = await client(`notes/${note.id}/match_delivery_ai`, {method: "GET"});
                if (cancelled) return;
                setCurrentNote(updated);
                if (!["QU", "IP"].includes(updated.status_match_delivery_ai)) {
                    if (updated.delivery) notify("Bolla associata automaticamente alla missione");
                    refresh();
                    return;
                }
            } catch (error) {
                // Un errore di rete non interrompe il job già avviato sul backend.
            }
            if (!cancelled) timer = setTimeout(poll, 3000);
        };
        timer = setTimeout(poll, 1500);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [processing, submitting, note.id, notify, refresh]);

    const startExtraction = async () => {
        setSubmitting(true);
        setCurrentNote({...currentNote, status_match_delivery_ai: "QU", ai_match: null});
        try {
            const updated = await client(`notes/${note.id}/match_delivery_ai`, {method: "POST"});
            setCurrentNote(updated);
        } catch (error) {
            setCurrentNote(currentNote);
            notify("Impossibile avviare l’associazione AI", "warning");
        } finally {
            setSubmitting(false);
        }
    };

    const assignProbableDelivery = async (deliveryId: Identifier) => {
        setAssigningDelivery(deliveryId);
        try {
            const updated = await client(`notes/${note.id}`, {
                method: "PATCH",
                data: {delivery: deliveryId},
            });
            setCurrentNote(updated);
            notify("Bolla associata alla missione");
            refresh();
        } catch (error) {
            notify("Impossibile associare la bolla alla missione", "warning");
        } finally {
            setAssigningDelivery(undefined);
        }
    };

    return (
        <Box mb={3} component={Paper} overflow="hidden" border={selected ? "2px solid #3f51b5" : "2px solid transparent"}>
            <Box display="flex" alignItems="center" flexWrap="wrap" p={1} bgcolor="#f5f5f5">
                <Radio checked={selected} onChange={onSelect} inputProps={{"aria-label": `Seleziona bolla ${note.id}`}}/>
                <Typography variant="subtitle2">Bolla #{note.id}</Typography>
                <Box flexGrow={1}/>
                <Tooltip title="Ruota bolla">
                    <IconButton aria-label="Ruota bolla" size="small" onClick={() => setRotation(rotation + 90)}>
                        <RotateRightIcon/>
                    </IconButton>
                </Tooltip>
                <Box mx={1}>
                    <Button size="small" color="primary" variant="outlined" disabled={processing || assigningDelivery !== undefined}
                            onClick={startExtraction}
                            startIcon={processing ? <CircularProgress size={16}/> : <BsStars/>}>
                        {processing ? "In corso" : currentNote.status_match_delivery_ai === "ER" ? "Riprova AI" : "Associa con AI"}
                    </Button>
                </Box>
                {currentNote.status_match_delivery_ai === "CO" && <Chip size="small" label="Estratta · da associare"/>}
                {currentNote.status_match_delivery_ai === "ER" && <Chip size="small" label="Errore AI" color="secondary"/>}
                <DeleteWithConfirmButton record={note} resource="notes" mutationMode="pessimistic"
                                         onSuccess={() => refresh()} label=""/>
            </Box>
            {result && !currentNote.delivery && (
                <Box p={2} bgcolor="#f3f7ff">
                    {result.error && <Typography color="error" variant="body2">{result.error}</Typography>}
                    <>
                        <Typography variant="body2"><strong>Documento:</strong> {result.document_number || "Non rilevato"}</Typography>
                        <Typography variant="body2"><strong>Destinatario:</strong> {receiver?.business_name || "Non rilevato"}</Typography>
                        <Typography variant="body2">{[receiver?.address, receiver?.postal_code, receiver?.locality, receiver?.province].filter(Boolean).join(" · ")}</Typography>
                        {receiver?.match && <Typography variant="caption" display="block">Destinatario suggerito: {receiver.match.label} ({Math.round(receiver.match.similarity * 100)}%)</Typography>}
                        <Typography variant="caption" display="block">{aiReasons[result.reason]}</Typography>
                        {result.probable_deliveries?.map((delivery: {
                            id: Identifier;
                            document_number: string;
                            delivery_date: string | null;
                            receiver_label: string;
                            client_label: string;
                            similarity: number;
                        }) => (
                            <Box key={delivery.id} mt={1} p={1} component={Paper} border={1} borderColor="grey.300"
                                 display="flex" alignItems="center" justifyContent="space-between">
                                <Box mr={1}>
                                    <Typography variant="body2"><strong>Missione #{delivery.id}</strong> · {delivery.document_number}</Typography>
                                    <Typography variant="body2">{delivery.receiver_label}</Typography>
                                    <Typography variant="caption" display="block">
                                        {[delivery.client_label, delivery.delivery_date && new Date(delivery.delivery_date).toLocaleDateString("it-IT")].filter(Boolean).join(" · ")}
                                        {` · Similarità ${Math.round(delivery.similarity * 100)}%`}
                                    </Typography>
                                </Box>
                                <Button color="primary" variant="outlined" size="small"
                                        disabled={assigningDelivery !== undefined || processing || submitting}
                                        onClick={() => assignProbableDelivery(delivery.id)}
                                        startIcon={assigningDelivery === delivery.id ? <CircularProgress size={14}/> : undefined}>
                                    Associa
                                </Button>
                            </Box>
                        ))}
                        {!result.document_number && !receiver?.business_name && result.output && (
                            <Box component="pre" mt={1} style={{whiteSpace: "pre-wrap", overflowWrap: "anywhere"}}>
                                {JSON.stringify(result.output, null, 2)}
                            </Box>
                        )}
                    </>
                </Box>
            )}
            <NoteImage src={note.file_info.url} rotation={rotation}/>
        </Box>
    );
};

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            width: "100%",
            height: "100%",
            overflow: "hidden",

            [theme.breakpoints.up("sm")]: {
                height: "100%",
                width: "100%",
            },
        },
        column: {
            padding: "0.5em",
            overflowY: "auto",
            maxHeight: "100%",
        },
    })
);

export const NotAssignedNotes = () => {
    const classes = useStyles();
    const refresh = useRefresh();
    const notify = useNotify();
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const {data} = useQueryWithStore({
        type: "getList",
        resource: "notes",
        payload: {
            sort: {field: "id", order: "DESC"},
            filter: {
                assigned: false,
            },
            pagination: {perPage: 30, page: 1},
        },
    });

    const [selectedNote, setSelectedNote] = useState<Identifier>();
    const startAllExtractions = async () => {
        setBulkSubmitting(true);
        let started = 0;
        let failed = 0;
        try {
            // Recupera tutte le bolle, anche oltre le 30 mostrate nella colonna.
            const notes: Record[] = await client("notes", {
                method: "GET",
                params: {assigned: false, no_page: true, ordering: "-id"},
            });
            const pending = notes.filter(note => !note.delivery && note.file_info?.name &&
                !["QU", "IP"].includes(note.status_match_delivery_ai));
            for (let offset = 0; offset < pending.length; offset += 5) {
                const results = await Promise.allSettled(pending.slice(offset, offset + 5).map(note =>
                    client(`notes/${note.id}/match_delivery_ai`, {method: "POST"})
                ));
                started += results.filter(result => result.status === "fulfilled").length;
                failed += results.filter(result => result.status === "rejected").length;
                refresh();
            }
            if (failed) {
                notify(`Elaborazione AI avviata per ${started} bolle. Avvio non riuscito per ${failed} bolle.`, "warning");
            } else {
                notify(started ? `Elaborazione AI avviata per ${started} bolle` : "Nessuna bolla da avviare: elaborazioni già in corso o file mancanti");
            }
        } catch (error) {
            notify("Impossibile avviare l’associazione AI delle bolle", "warning");
        } finally {
            setBulkSubmitting(false);
            refresh();
        }
    };
    useEffect(() => {
        if (data && selectedNote && !data.some((note: Record) => note.id === selectedNote)) {
            setSelectedNote(undefined);
        }
    }, [data, selectedNote]);

    useClosedSidebar();
    return (
        <Grid container className={classes.root}>
            <Grid container item xs={12} md={6} className={classes.column}>
                <Grid item xs={12}>
                    <Box component={Paper} p={2} mb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                        <Typography variant="subtitle1">Bolle non associate</Typography>
                        <Button color="primary" variant="contained" disabled={bulkSubmitting || !data?.length}
                                onClick={startAllExtractions}
                                startIcon={bulkSubmitting ? <CircularProgress size={16} color="inherit"/> : <BsStars/>}>
                            {bulkSubmitting ? "Avvio in corso…" : "Associa tutto con AI"}
                        </Button>
                    </Box>
                </Grid>
                {data && data.length <= 0 && (
                    <Grid item xs={12}>
                        <Empty resource="notes"/>
                    </Grid>
                )}
                {data && data.map((note: Record) => (
                    <Grid item xs={12} key={note.id}>
                        <UnassignedNoteCard note={note} selected={selectedNote === note.id}
                                            onSelect={() => setSelectedNote(note.id)}/>
                    </Grid>
                ))}
            </Grid>
            <Grid item xs={12} md={6} className={classes.column}>
                <DeliveryList
                    selectedNote={selectedNote}
                    setSelectedNote={setSelectedNote}
                />
            </Grid>
        </Grid>
    );
};
